import type { Judgement, NoteResult } from '../analysis/analyze';

export interface ScoreOptions {
  /** Points per judgement, 0 to 1. */
  points: Record<Judgement, number>;
  /** Weight of pitch in the overall score; rhythm gets the rest. */
  pitchWeight: number;
  /** Bars scoring below this (0 to 1) are listed as weak. */
  weakBarBelow: number;
  /** How many weak bars to list. */
  weakBarCount: number;
  /** Stretches of the take this long (seconds) are compared to find a shift in its timing. */
  shiftWindowSec: number;
  /** A stretch counts only with at least this many starts. */
  shiftMinStarts: number;
  /** A shift above this (ms) between stretches is reported (see timingShift). */
  shiftWarnMs: number;
}

export const DEFAULT_SCORE_OPTIONS: ScoreOptions = {
  points: { good: 1, close: 0.5, wrong: 0 },
  pitchWeight: 0.6,
  weakBarBelow: 0.85,
  weakBarCount: 3,
  // A delay change lasts: in a Bluetooth take of October 2026 the starts were 160–290 ms late for the
  // first 30 seconds and on time afterwards, and in silent takes on 31 October 2026 (one earbud held
  // against the microphone, the earbuds also connected to a phone) the delay once started about
  // 220 ms high and fell back over about 40 seconds. A singer's timing wanders back and forth.
  // Stretches of 15 seconds, chosen on 2 November 2026 on the 41 takes of the tuning set and one of
  // the owner's: the Bluetooth take differs by 239 ms, the others by 115 ms at most. Runs of 8
  // starts, before, put the Bluetooth take at 246 ms but long fast takes up to 170 ms, as many runs
  // give two far apart (a take whose singer turned up the headset volume after 20 seconds).
  shiftWindowSec: 15,
  shiftMinStarts: 6,
  shiftWarnMs: 150,
};

export interface TakeScores {
  /** 0 to 100. */
  overall: number;
  pitch: number;
  /** Null when no onset could be judged (e.g. everything legato on one pitch). */
  rhythm: number | null;
  noteCount: number;
  pitchGood: number;
  pitchClose: number;
  pitchWrong: number;
  missed: number;
  rhythmJudged: number;
  late: number;
  early: number;
}

export interface BarIssues {
  missed: number;
  flat: number;
  sharp: number;
  octave: number;
  late: number;
  early: number;
  /**
   * Mean cents of the flat notes (negative), or null: since browser-1.5.0 how far they were while
   * out of tune (NoteResult.outCents), before that their median.
   */
  flatCents: number | null;
  /** Mean cents of the sharp notes, or null (as flatCents). */
  sharpCents: number | null;
  /**
   * Since browser-1.5.0: how long the flat notes were flat, the mean percent of each note
   * (NoteResult.outShare), or null; likewise the sharp notes. Absent in older results.
   */
  flatPct?: number | null;
  sharpPct?: number | null;
}

export interface WeakBar {
  timelineMeasureIndex: number;
  measureIndex: number;
  measureNumber: string;
  /** 0 to 1. */
  score: number;
  noteCount: number;
  issues: BarIssues;
}

export interface TakeScoring {
  scores: TakeScores;
  weakestBars: WeakBar[];
  /**
   * How far the timing of the take's starts shifted between its parts (ms), when that is more than
   * an even take shows; otherwise null (see timingShift). Missing in takes scored before browser-1.3.0.
   */
  timingShiftMs?: number | null;
}

export type SuggestedGrade = 'READY' | 'ALMOST' | 'NEEDS_WORK';

export interface GradeThresholds {
  ready: number;
  almost: number;
}

/**
 * Ready from 80 (85 until 2 November 2026, the owner's decision): rhythm is nearly always 90 or more
 * and counts 40 %, so overall scores sit between about 75 and 93, and at 85 most good takes stayed
 * Almost. 80 is about 60 % of the notes good, 30 % close and 10 % wrong.
 */
export const DEFAULT_GRADE_THRESHOLDS: GradeThresholds = { ready: 80, almost: 70 };

/** A wrong note within this many cents of an octave above or below is an octave error. */
const OCTAVE_MATCH_CENTS = 50;

/** What one note adds to the overall score, 0 to 1: pitch and rhythm weighted, or pitch alone when the rhythm was not judged. */
export function notePoints(r: NoteResult, o: ScoreOptions = DEFAULT_SCORE_OPTIONS): number {
  if (r.status === 'missed') return 0;
  const pitch = o.points[r.pitch as Judgement];
  if (r.rhythm === null) return pitch;
  return o.pitchWeight * pitch + (1 - o.pitchWeight) * o.points[r.rhythm];
}

/** A pitch error (cents) that is a whole octave off rather than out of tune: counted as an octave error. */
export function isOctaveError(cents: number): boolean {
  const folded = cents - 1200 * Math.round(cents / 1200);
  return Math.abs(cents) >= 1100 && Math.abs(folded) <= OCTAVE_MATCH_CENTS;
}

function issuesOf(results: readonly NoteResult[]): BarIssues {
  const issues: BarIssues = { missed: 0, flat: 0, sharp: 0, octave: 0, late: 0, early: 0, flatCents: null, sharpCents: null };
  const flat: number[] = [];
  const sharp: number[] = [];
  const flatShare: number[] = [];
  const sharpShare: number[] = [];
  for (const r of results) {
    if (r.status === 'missed') {
      issues.missed++;
      continue;
    }
    if (r.pitch !== 'good' && r.cents !== null) {
      if (isOctaveError(r.cents)) issues.octave++;
      else if (typeof r.outShare === 'number' && typeof r.outCents === 'number') {
        // Since browser-1.5.0: the side the note was out of tune on, for how long and how far.
        (r.outCents < 0 ? flat : sharp).push(r.outCents);
        (r.outCents < 0 ? flatShare : sharpShare).push(r.outShare * 100);
      } else if (r.cents < 0) flat.push(r.cents);
      else sharp.push(r.cents);
    }
    if (r.rhythm !== null && r.rhythm !== 'good' && r.onsetMs !== null) {
      if (r.onsetMs > 0) issues.late++;
      else issues.early++;
    }
  }
  issues.flat = flat.length;
  issues.sharp = sharp.length;
  const mean = (xs: number[]) => (xs.length ? Math.round(xs.reduce((s, x) => s + x, 0) / xs.length) : null);
  issues.flatCents = mean(flat);
  issues.sharpCents = mean(sharp);
  if (flatShare.length || sharpShare.length) {
    issues.flatPct = mean(flatShare);
    issues.sharpPct = mean(sharpShare);
  }
  return issues;
}

/**
 * A shift in the timing of a take's starts: the audio delay may have changed during it (a Bluetooth
 * headset settling or jumping), or the singer drifted that far for a while. The middle value
 * (median) of the starts in every stretch of `windowSec` seconds (from each start) is taken, and the
 * largest difference between two of them, in either direction, is returned (rounded to 10 ms) when
 * it is above `warnMs`; otherwise null. A stretch counts with at least `minStarts` starts spread
 * over most of it (60%, so the take's last seconds are not a stretch of their own); with fewer than
 * two, nothing is reported. Comparing stretches of time, not runs of starts, reports a delay that
 * moves and stays moved, and not a singer's back-and-forth, however many notes the song has.
 */
export function timingShift(results: readonly NoteResult[], windowSec: number, minStarts: number, warnMs: number): number | null {
  const starts = results.flatMap((r) => (r.onsetMs === null ? [] : [{ t: r.startSec, ms: r.onsetMs }])).sort((a, b) => a.t - b.t);
  let low = Infinity;
  let high = -Infinity;
  let stretches = 0;
  for (let i = 0; i < starts.length; i++) {
    const from = (starts[i] as { t: number }).t;
    const inside: number[] = [];
    let last = from;
    for (let k = i; k < starts.length && (starts[k] as { t: number }).t < from + windowSec; k++) {
      inside.push((starts[k] as { ms: number }).ms);
      last = (starts[k] as { t: number }).t;
    }
    if (inside.length < minStarts || last - from < 0.6 * windowSec) continue;
    inside.sort((a, b) => a - b);
    const mid = inside.length >> 1;
    const median = inside.length % 2 ? (inside[mid] as number) : ((inside[mid - 1] as number) + (inside[mid] as number)) / 2;
    low = Math.min(low, median);
    high = Math.max(high, median);
    stretches++;
  }
  return stretches >= 2 && high - low > warnMs ? Math.round((high - low) / 10) * 10 : null;
}

/**
 * Overall, pitch and rhythm scores from 0 to 100, and the weakest bars. Missed notes count as
 * wrong for pitch; rhythm is judged only on notes whose onset could be heard.
 */
export function scoreTake(results: readonly NoteResult[], options: Partial<ScoreOptions> = {}): TakeScoring {
  const o = { ...DEFAULT_SCORE_OPTIONS, ...options };
  const n = results.length;
  let pitchPoints = 0;
  let rhythmPoints = 0;
  const s: TakeScores = {
    overall: 0,
    pitch: 0,
    rhythm: null,
    noteCount: n,
    pitchGood: 0,
    pitchClose: 0,
    pitchWrong: 0,
    missed: 0,
    rhythmJudged: 0,
    late: 0,
    early: 0,
  };
  for (const r of results) {
    if (r.pitch === 'missed') s.missed++;
    else {
      if (r.pitch === 'good') s.pitchGood++;
      else if (r.pitch === 'close') s.pitchClose++;
      else s.pitchWrong++;
      pitchPoints += o.points[r.pitch];
    }
    if (r.rhythm !== null) {
      s.rhythmJudged++;
      rhythmPoints += o.points[r.rhythm];
      if (r.rhythm !== 'good' && r.onsetMs !== null) {
        if (r.onsetMs > 0) s.late++;
        else s.early++;
      }
    }
  }
  s.pitch = n > 0 ? Math.round((pitchPoints / n) * 100) : 0;
  s.rhythm = s.rhythmJudged > 0 ? Math.round((rhythmPoints / s.rhythmJudged) * 100) : null;
  s.overall = s.rhythm === null ? s.pitch : Math.round(o.pitchWeight * s.pitch + (1 - o.pitchWeight) * s.rhythm);

  // Weakest bars: per played bar (a repeated bar counts per pass).
  const byBar = new Map<number, NoteResult[]>();
  for (const r of results) {
    const list = byBar.get(r.timelineMeasureIndex);
    if (list) list.push(r);
    else byBar.set(r.timelineMeasureIndex, [r]);
  }
  const bars: WeakBar[] = [];
  for (const [timelineMeasureIndex, list] of byBar) {
    const score = list.reduce((sum, r) => sum + notePoints(r, o), 0) / list.length;
    if (score >= o.weakBarBelow) continue;
    const first = list[0] as NoteResult;
    bars.push({
      timelineMeasureIndex,
      measureIndex: first.measureIndex,
      measureNumber: first.measureNumber,
      score: Math.round(score * 1000) / 1000,
      noteCount: list.length,
      issues: issuesOf(list),
    });
  }
  bars.sort((a, b) => a.score - b.score || a.timelineMeasureIndex - b.timelineMeasureIndex);
  return { scores: s, weakestBars: bars.slice(0, o.weakBarCount), timingShiftMs: timingShift(results, o.shiftWindowSec, o.shiftMinStarts, o.shiftWarnMs) };
}

/** The grade the app suggests to the director (Hazır / Neredeyse / Çalışmalı). */
export function suggestGrade(overall: number, thresholds: GradeThresholds = DEFAULT_GRADE_THRESHOLDS): SuggestedGrade {
  if (overall >= thresholds.ready) return 'READY';
  if (overall >= thresholds.almost) return 'ALMOST';
  return 'NEEDS_WORK';
}
